import { applyScalarDelta, emptyScalarWorld } from '@efira/protocol';
import { mountScene } from './scene.js';
/**
 * Подстановка `{name}` в строку словаря.
 *
 * Своя, а не библиотека интернационализации: подстановка по имени - это всё,
 * что используют десять игр, а полноценная библиотека стоит килобайты в
 * бюджете первой загрузки overlay и ничего сверх этого не даёт.
 */
export function interpolate(template, params) {
    if (params === undefined) {
        return template;
    }
    return template.replace(/\{(\w+)\}/g, (whole, key) => {
        const value = params[key];
        return value === undefined ? whole : String(value);
    });
}
/**
 * Накопитель мира класса `scalar` с обнаружением разрывов.
 *
 * Отдельная сущность, а не три переменные внутри `mountGame`, потому что она
 * и есть реализация правил 1 и 5 раздела 3.2, и её надо уметь проверить
 * тестом без DOM.
 */
export class ScalarWorldStore {
    current = emptyScalarWorld();
    tick = -1;
    epoch = -1;
    world() {
        return this.current;
    }
    lastTick() {
        return this.tick < 0 ? null : this.tick;
    }
    /**
     * Принимает кадр. Возвращает причину ресинхронизации, если кадр применить
     * нельзя, и `null`, если мир обновлён.
     *
     * ПОЧЕМУ РАЗРЫВ ЛОВИТСЯ ЗДЕСЬ, А НЕ ИГНОРИРУЕТСЯ. Дельта, наложенная поверх
     * мира, в котором пропущен один пакет, не падает и не жалуется - она даёт
     * ТИХО НЕВЕРНУЮ картинку: счёт разошёлся на единицу, дорожка отстала на
     * десятую. Такой дефект не воспроизводится по жалобе и не ловится тестом;
     * единственная защита - сверка номера тика на каждом кадре.
     */
    accept(frame) {
        if (frame.full !== undefined) {
            this.current = frame.full;
            this.tick = frame.simTick;
            this.epoch = frame.matchEpoch;
            return null;
        }
        if (frame.matchEpoch !== this.epoch) {
            // Эпоха сменилась, а полного снимка не было: он потерян или мы
            // подключились посреди матча. Дельта к чужой эпохе бессмысленна.
            return 'epoch';
        }
        if (this.tick >= 0 && frame.simTick !== this.tick + 1) {
            return 'gap';
        }
        this.current = applyScalarDelta(this.current, frame.delta);
        this.tick = frame.simTick;
        return null;
    }
    /** Сброс при размонтировании: мир не переживает сцену. */
    reset() {
        this.current = emptyScalarWorld();
        this.tick = -1;
        this.epoch = -1;
    }
}
/**
 * Монтирует игру. Возвращает ту же ручку, что и сцена: снятие - один вызов.
 *
 * Игра оборачивается в сцену, а не подменяет её: весь механизм освобождения
 * ресурсов - кадры, таймеры, слушатели, `defer` - работает без единого
 * исключения, а подписки транспорта снимаются через тот же `defer`.
 */
export async function mountGame(game, options) {
    /**
     * Дальше по коду мир - `unknown`, и это осознанно.
     *
     * Внутри монтирования мир не читают: его кладут в накопитель, достают из
     * накопителя и передают игре. Все три операции безразличны к классу. Типы
     * сходятся на границах - у накопителя, объявленного игрой, и у обработчиков,
     * которые игра пишет сама, - а посередине единственная честная запись это
     * «что бы там ни было, оно проходит насквозь».
     *
     * Альтернатива - сделать `mountGame` обобщённым и вывести класс из игры -
     * выглядит строже, но заставила бы overlay ЗНАТЬ класс мира каждой игры в
     * месте, где он грузит модуль динамически, то есть не знает даже её имени.
     */
    const store = (game.createStore?.() ?? new ScalarWorldStore());
    let contract = options.contract;
    const members = new Map(Object.entries(options.members ?? {}));
    const stateHandlers = [];
    const actionHandlers = [];
    const chatHandlers = [];
    const contractHandlers = [];
    const memberHandlers = [];
    /**
     * Команда, уже исполненная, не исполняется второй раз.
     *
     * Overlay переподключается, и сервер вправе доставить команду повторно.
     * Правило идемпотентности объявлено в форме `ActionCommand`; исполнять его
     * здесь, а не в каждой игре, - единственный способ, которым оно доживёт до
     * десятой игры.
     */
    const seenCommands = new Set();
    const scene = {
        id: game.id,
        mount: async (base) => {
            const context = {
                ...base,
                gameId: game.id,
                get contract() {
                    return contract;
                },
                world: () => store.world(),
                t: (key, params) => interpolate(contract.dictionary[key] ?? key, params),
                member: (id) => members.get(id),
                onState: (handler) => stateHandlers.push(handler),
                onAction: (handler) => actionHandlers.push(handler),
                onChat: (handler) => chatHandlers.push(handler),
                onContract: (handler) => contractHandlers.push(handler),
                onMembers: (handler) => memberHandlers.push(handler),
            };
            const offState = options.transport.onState((frame) => {
                const reason = store.accept(frame, performance.now());
                if (reason !== null) {
                    options.transport.resync(reason, store.lastTick());
                    return;
                }
                const delta = frame.full === undefined ? frame.delta : null;
                for (const handler of stateHandlers) {
                    handler(store.world(), delta);
                }
            });
            const offAction = options.transport.onAction((command) => {
                if (seenCommands.has(command.id)) {
                    return;
                }
                seenCommands.add(command.id);
                // Множество не растёт бесконечно: эфир идёт часами, а команд в час
                // могут быть тысячи. Порог с запасом больше окна переподключения.
                if (seenCommands.size > 4096) {
                    seenCommands.clear();
                }
                for (const handler of actionHandlers) {
                    handler(command);
                }
            });
            const offChat = options.transport.onChat((line) => {
                for (const handler of chatHandlers) {
                    handler(line);
                }
            });
            const offContract = options.transport.onContract((next) => {
                contract = next;
                for (const handler of contractHandlers) {
                    handler(next);
                }
            });
            const offMembers = options.transport.onMembers((incoming) => {
                const ids = [];
                for (const [id, member] of Object.entries(incoming)) {
                    members.set(id, member);
                    ids.push(id);
                }
                for (const handler of memberHandlers) {
                    handler(ids);
                }
            });
            base.defer(() => {
                offState();
                offAction();
                offChat();
                offContract();
                offMembers();
                stateHandlers.length = 0;
                actionHandlers.length = 0;
                chatHandlers.length = 0;
                contractHandlers.length = 0;
                memberHandlers.length = 0;
                members.clear();
                seenCommands.clear();
                store.reset();
            });
            await game.mount(context);
            // Запрос полного снимка - ПОСЛЕ монтирования, а не до: игра должна
            // успеть подписаться, иначе первый же кадр уйдёт в пустоту, а следующий
            // придёт только когда мир изменится. У игры с паузой между раундами это
            // секунды пустого экрана.
            options.transport.resync('mount', null);
        },
    };
    return mountScene(scene, options.container, options.config);
}
//# sourceMappingURL=game.js.map